import { FILTERED_LISTING_MODULE_TYPE } from '@/app/lib/analytics/constants'

/**
 * `page_type` de esta PLP (enum de `dictionary/analytic-variables.md`) — mismo
 * criterio que Promociones y lo-mas-vendido: `plp`, para que todos los listados
 * caigan en los mismos informes. De aquí se derivan `item_list_id` y los
 * `component_id` de esta página, así que cambiarlo los mueve a la vez.
 */
export const ANALYTICS_PAGE_TYPE = 'plp'

/** `module_name` del listado principal en todos los eventos de esta página. */
export const ANALYTICS_MODULE_NAME = 'listado_agenda'

/**
 * `item_list_id` = `<page_type>_<module_type>` (`Listado filtrado.csv` →
 * «Click en card del listado»). Se preserva del `view_item_list` al
 * `select_item` para poder reconstruir el funnel. Con esa fórmula el valor
 * coincide con el de Promociones y lo-mas-vendido (mismo `page_type` y mismo
 * `module_type`); lo que distingue a los tres listados es `item_list_name` +
 * `page_hierarchy`. Se mantiene la fórmula del CSV en vez de inventar un id.
 */
export const ANALYTICS_ITEM_LIST_ID = `${ANALYTICS_PAGE_TYPE}_${FILTERED_LISTING_MODULE_TYPE}`

/** Nombre descriptivo del listado. Literal estable: NO varía por provincia ni
 * por día (ambos ya viajan en `page_hierarchy` y en el `filter_change`), para
 * no disparar la cardinalidad. */
export const ANALYTICS_ITEM_LIST_NAME = 'listado_de_agenda'

/**
 * `module_position` de los dos módulos de producto de la página por su posición
 * vertical en el layout: el carril de vistos recientemente y el listado
 * principal (en ese orden — ver `AgendaPageContent`).
 *
 * Es la posición del SLOT en el layout, no el recuento de módulos realmente
 * renderizados: "Vistos recientemente" desaparece si el visitante no tiene
 * historial. Si se contara en tiempo de render, la posición del listado
 * principal oscilaría entre 1 y 2 según una cookie y la dimensión dejaría de
 * ser comparable entre sesiones.
 */
export const ANALYTICS_RECENTLY_VIEWED_MODULE_POSITION = '1'
export const ANALYTICS_MODULE_POSITION = '2'

/**
 * `component_id`/`component_label` del `ui_interaction` de apertura y cierre del
 * calendario completo (`AgendaCalendar` → popover de `DatePicker`). El id sigue
 * la fórmula `<page_type>_<componente>` del CSV (igual que el
 * `<page_type>_view_toggle` del toggle de vista).
 */
export const ANALYTICS_CALENDAR_COMPONENT_ID = `${ANALYTICS_PAGE_TYPE}_agenda_calendar`
export const ANALYTICS_CALENDAR_COMPONENT_LABEL = 'calendario'

/**
 * `filter_type` del filtro de Subcategoría.
 *
 * ⚠️ PROVISIONAL — pendiente de Analítica. El enum de `filter_type`
 * (`components-fingerprint/Listado filtrado.csv` y
 * `dictionary/analytic-variables.md`) solo contempla `provincia | categoria |
 * fecha | precio | mes | sort`: agenda es la única PLP con un segundo nivel de
 * categoría, y no hay valor documentado para él. Se usa un valor propio en vez
 * de reutilizar `categoria` — colapsar los dos filtros en la misma dimensión
 * haría ambiguo `filter_previous_value` en los informes. Centralizado aquí para
 * sustituirlo por el valor canónico sin tocar los componentes.
 */
export const ANALYTICS_SUBGENRE_FILTER_TYPE = 'subcategoria'

/** Mapa campo→parámetro legible de la URL (sin sufijos técnicos como
 * `_nicename` — igual que `lo-mas-vendido`). Única fuente de verdad compartida
 * entre `parseAgendaFilters` (server) y `AgendaFilters` (cliente,
 * `useListingFieldFilters`). `dateRange` (añadido 2026-09-14) es el rango
 * rápido de chips (Hoy/Semana/Finde/Mes/Mes que viene) — bajo exclusión mutua
 * con `DATE_READABLE_PARAMS` (el calendario), nunca los dos a la vez en la
 * URL (ver `parseAgendaFilters`). */
export const READABLE_FILTER_PARAMS = {
  genre: 'genre',
  subgenre: 'subgenre',
  dateRange: 'date_range',
} as const

/** Mapa de `useListingFieldFilters` para la fecha del calendario
 * (`AgendaCalendar`) — instancia independiente de `READABLE_FILTER_PARAMS`: la
 * fecha no es un filtro de `ListingFilters`, se gestiona con su propio hook.
 * `start_at` (única fecha, o inicio de un rango) sustituye al antiguo `day`
 * único — mismo nombre que el filtro real de `getAgendaPage`/`/data`, sin
 * traducción de por medio; `end_at` solo aparece con un rango de dos fechas
 * ya cerrado (selección de múltiples fechas al estilo reserva de vuelos,
 * fiel al diseño de referencia `eci-firefly-uikit`). */
export const DATE_READABLE_PARAMS = {
  startAt: 'start_at',
  endAt: 'end_at',
} as const

/** Mapa clave del bloque en `filters` → clave real en `applied_filters`, para
 * `sanitizeAppliedFilters` (`@/app/lib/api/sanitizeAppliedFilters`, genérico). Sin
 * entrada para `province`: su fuente de verdad es la propia URL (segmento
 * `[location]`), igual que en `lo-mas-vendido`. */
export const APPLIED_FILTER_KEYS_MAP = {
  genre: 'genre_nicename',
  subgenre: 'subgenre_nicename',
  date_range: 'date_range',
} as const

/**
 * Fila (0-based) del listado principal tras la que se inserta la franja "Los
 * más buscados" (módulo `Productos`, reutilizado tal cual — ver
 * `AgendaPageContent`/`VirtualizedList.insertAfterRow`): `2` = después de la
 * 3ª fila, antes de la 4ª. Solo se pinta si además queda al menos una fila de
 * productos después — con menos de 4 filas para el breakpoint actual, no se
 * muestra (cada vista decide por su cuenta, ver `VirtualizedList`).
 */
export const AGENDA_MOST_SEARCHED_INSERT_AFTER_ROW = 2

/**
 * Título de la franja "Los más buscados" — pasado como `title` al módulo
 * `Productos` (que además antepone su propio antetítulo fijo "Recomendados",
 * inherente a ese componente reutilizado tal cual, sin fork).
 */
export const AGENDA_MOST_SEARCHED_TITLE = 'Los más buscados'

/** Nombre accesible del `LoadingOverlay` que pinta `GeoLocationSync` mientras
 * redirige a la provincia de la cookie `location` y recarga el listado —
 * mismo criterio que Promociones: sin overlay, el visitante vería un
 * instante la agenda sin filtrar antes del salto a la localizada. */
export const GEO_LOCATION_LOADING_LABEL = 'Cargando la agenda de tu ubicación'

/** `<title>` de Agenda, fijo (igual en `/agenda` y `/agenda/[location]`). */
export const AGENDA_PAGE_TITLE = 'Agenda | Entradas El Corte Inglés'
